// Сборка строк для нового пункта меню "Скачать" на вкладке "Справки (расходы)" (УО) -
// выгрузка в Excel со ВСЕМИ строками справки (абп/прг/ппр/спф/проект), включая примечания (EAK-623).
//
// Бэкенд-эндпоинт POST /api-py/alteration-reference-expenses/ ожидает уже готовое дерево строк
// (см. newAlterationForm.vue, downloadReferenceExpenses()). Источник данных - те же плоские массивы
// formObligation/formPayment, из которых строит дерево c-abp-prg-tree-table (разрез "по АБП",
// tableType=2, см. CAbpPrgTreeTable.vue), но то дерево строится ВНУТРИ компонента и наружу не
// эмиттится (в отличие от c-prg-tree-table, которое прокидывает @getTree) - поэтому группировку
// приходится повторить здесь по тому же алгоритму (getTreeData() в CAbpPrgTreeTable.vue):
// abp -> prg -> ppr, со схлопыванием одинаковых специфик внутри (abp, prg, ppr), которые отличались
// только уровнем ГУ (в разрезе "по АБП" ГУ не учитывается).
//
// Отличие от экрана: строка уровня ппр с ppr === null|0 (служебная группировка "подпрограммы нет",
// на экране подписывается заглушкой '000') в экспорт не включается - её специфики становятся
// прямыми детьми строки программы (так в эталонном шаблоне справки).

import {
    IReasonGroup,
    ReasonPlanType,
    referenceReasonKey,
    reasonGroupText
} from '@/modules/budget/alteration/form/tabs/components/CreateForm/reasons/alteration-reason-api';

export type ReferenceExpenseLevel = 'abp' | 'prg' | 'ppr' | 'spf' | 'project';

export interface IMonthTotalsDto {
    month0: number;
    month1: number;
    month2: number;
    month3: number;
    month4: number;
    month5: number;
    month6: number;
    month7: number;
    month8: number;
    month9: number;
    month10: number;
    month11: number;
}

export interface IReferenceExpenseRow extends IMonthTotalsDto {
    level: ReferenceExpenseLevel;
    code: string | number | null;
    name: string;
    reasonText: string | null;
}

export interface IReferenceExpensesSheet {
    rows: IReferenceExpenseRow[];
    totals: IMonthTotalsDto;
}

const MONTH_COUNT = 12;

function emptyMonths(): IMonthTotalsDto {
    return {
        month0: 0,
        month1: 0,
        month2: 0,
        month3: 0,
        month4: 0,
        month5: 0,
        month6: 0,
        month7: 0,
        month8: 0,
        month9: 0,
        month10: 0,
        month11: 0
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function addMonths(target: IMonthTotalsDto, src: any): void {
    for (let i = 0; i < MONTH_COUNT; i++) {
        const key = `month${i}`;
        const value = src[key];
        if (value) {
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (target as any)[key] = Math.round((numValue + (target as any)[key]) * 10) / 10;
        }
    }
}

// Локализованное название с fallback на русское (повторяет getLocalizedExpenseName в CAbpPrgTreeTable.vue:
// там тоже делают "||" fallback на _ru - на kk заполнено не всегда)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function localized(item: any, fieldBase: string, lang: string): string {
    return item[`${fieldBase}_${lang}`] || item[`${fieldBase}_ru`] || '';
}

/** Числовой компаратор, допускающий null (справочно - подпрограмма без ппр); повторяет наивную
 * сортировку (a - b > 0 ? 1 : -1), применяемую в CAbpPrgTreeTable.vue для prg/ppr/spf */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function byCodeAsc(a: any, b: any): number {
    return (a - b > 0 ? 1 : -1);
}

/**
 * Строит одну "страницу" (обязательства ИЛИ платежи) по контракту POST /api-py/alteration-reference-expenses/.
 *
 * @param tableData     formObligation либо formPayment (плоский массив строк специфик+проектов)
 * @param reasonGroups  примечания справки, сгруппированные по referenceReasonKey (см. loadReferenceReasons)
 * @param reasonPlanType 1 - обязательства, 2 - платежи
 * @param lang          'ru' | 'kk' - язык названий и текста примечаний (= this.$i18n.locale на момент клика)
 */
export function buildReferenceExpensesSheet(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tableData: any[] | null | undefined,
    reasonGroups: Record<string, IReasonGroup>,
    reasonPlanType: ReasonPlanType,
    lang: string
): IReferenceExpensesSheet {
    const rows: IReferenceExpenseRow[] = [];
    const totals = emptyMonths();

    if (!tableData || !tableData.length) {
        return { rows, totals };
    }

    // Глубокое клонирование - слияние одинаковых ПРГ/ППР/СПФ не должно мутировать formObligation/formPayment,
    // т.к. это реальные, используемые на экране (и для сохранения) данные компонента.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any[] = JSON.parse(JSON.stringify(tableData));

    const reasonTextFor = (abp: unknown, prg: unknown, ppr: unknown, spf: unknown, expenseCode: string | null): string | null => {
        const key = referenceReasonKey(abp, prg, ppr, spf, expenseCode, reasonPlanType);
        const text = reasonGroupText(reasonGroups[key] || null, lang);
        return text || null;
    };

    // 1. Группировка по АБП
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapAbp = new Map<unknown, any>();
    for (const el of data) {
        let abpObj = mapAbp.get(el.abp);
        if (!abpObj) {
            abpObj = { abp: el.abp, name: localized(el, 'abp_name', lang), vals: [el] };
            mapAbp.set(el.abp, abpObj);
        } else {
            abpObj.vals.push(el);
        }
    }

    // 2. Группировка по программам внутри АБП
    for (const abpObj of mapAbp.values()) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mapPrg = new Map<unknown, any>();
        for (const elPrg of abpObj.vals) {
            let prgObj = mapPrg.get(elPrg.prg);
            if (!prgObj) {
                prgObj = { prg: elPrg.prg, name: localized(elPrg, 'prg_name', lang), vals: [elPrg] };
                mapPrg.set(elPrg.prg, prgObj);
            } else {
                prgObj.vals.push(elPrg);
            }
        }
        abpObj.vals = mapPrg;
    }

    // 3. Группировка по подпрограммам внутри (АБП, ПРГ)
    for (const abpObj of mapAbp.values()) {
        for (const [keyPrg, prgObj] of abpObj.vals) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const mapPpr = new Map<unknown, any>();
            for (const elPpr of prgObj.vals) {
                let pprObj = mapPpr.get(elPpr.ppr);
                if (!pprObj) {
                    pprObj = { ppr: elPpr.ppr, name: localized(elPpr, 'ppr_name', lang), vals: [elPpr] };
                    mapPpr.set(elPpr.ppr, pprObj);
                } else {
                    pprObj.vals.push(elPpr);
                }
            }
            abpObj.vals.set(keyPrg, { ...prgObj, vals: mapPpr });
        }
    }

    const abpList = Array.from(mapAbp.values()).sort((a, b) => byCodeAsc(a.abp, b.abp));

    for (const abpObj of abpList) {
        const abpRow: IReferenceExpenseRow = {
            level: 'abp', code: abpObj.abp, name: abpObj.name, reasonText: null, ...emptyMonths()
        };
        rows.push(abpRow);

        const prgList = Array.from(abpObj.vals.values()).sort((a: any, b: any) => byCodeAsc(a.prg, b.prg));

        for (const prgObj of prgList as any[]) {
            const prgRow: IReferenceExpenseRow = {
                level: 'prg', code: prgObj.prg, name: prgObj.name, reasonText: null, ...emptyMonths()
            };
            rows.push(prgRow);

            const pprList = Array.from(prgObj.vals.values()).sort((a: any, b: any) => byCodeAsc(a.ppr, b.ppr));

            for (const pprObj of pprList as any[]) {
                // Подпрограммы нет (служебная группировка для UI, на экране - заглушка '000'):
                // строку уровня ppr не создаём, специфики идут прямыми детьми программы (см. комментарий выше)
                const hasRealPpr = pprObj.ppr !== null && pprObj.ppr !== undefined && pprObj.ppr !== 0;
                let pprRow: IReferenceExpenseRow | null = null;
                if (hasRealPpr) {
                    pprRow = { level: 'ppr', code: pprObj.ppr, name: pprObj.name, reasonText: null, ...emptyMonths() };
                    rows.push(pprRow);
                }

                // Схлопывание одинаковых специфик внутри (abp, prg, ppr) - несколько исходных строк
                // могли отличаться только ГУ, который в разрезе "по АБП" не учитывается
                // (повторяет merge-логику getTreeData() в CAbpPrgTreeTable.vue).
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const spfMap = new Map<unknown, any>();
                for (const spfEl of pprObj.vals) {
                    const existing = spfMap.get(spfEl.spf);
                    if (!existing) {
                        spfMap.set(spfEl.spf, spfEl);
                    } else {
                        addMonths(existing, spfEl);
                        if (spfEl.child_list?.length) {
                            if (!existing.child_list) { existing.child_list = []; }
                            for (const childEl of spfEl.child_list) {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                const existingChild = existing.child_list.find((c: any) => c.expense_code === childEl.expense_code);
                                if (existingChild) {
                                    addMonths(existingChild, childEl);
                                } else {
                                    existing.child_list.push(childEl);
                                }
                            }
                        }
                    }
                }

                const spfList = Array.from(spfMap.values()).sort((a: any, b: any) => byCodeAsc(a.spf, b.spf));

                for (const spfEl of spfList as any[]) {
                    const spfRow: IReferenceExpenseRow = {
                        level: 'spf',
                        code: spfEl.spf,
                        name: localized(spfEl, 'name', lang),
                        reasonText: reasonTextFor(abpObj.abp, prgObj.prg, pprObj.ppr, spfEl.spf, null),
                        ...emptyMonths()
                    };
                    addMonths(spfRow, spfEl);
                    rows.push(spfRow);

                    // Накопление итогов по цепочке abp/prg/ppr - только по "своим" значениям специфики,
                    // без повторного сложения проектов (проекты - разбивка той же суммы, не добавка к ней;
                    // так же считает totalSum в CAbpPrgTreeTable.vue - getSumTotal вызывается только для spfEl)
                    addMonths(abpRow, spfRow);
                    addMonths(prgRow, spfRow);
                    if (pprRow) { addMonths(pprRow, spfRow); }

                    for (const chldEl of spfEl.child_list || []) {
                        const projectRow: IReferenceExpenseRow = {
                            level: 'project',
                            code: chldEl.expense_code,
                            name: localized(chldEl, 'expense_name', lang),
                            reasonText: reasonTextFor(abpObj.abp, prgObj.prg, pprObj.ppr, spfEl.spf, chldEl.expense_code),
                            ...emptyMonths()
                        };
                        addMonths(projectRow, chldEl);
                        rows.push(projectRow);
                    }
                }
            }
        }

        addMonths(totals, abpRow);
    }

    return { rows, totals };
}
